import React from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Award } from 'lucide-react';
import { getCompletionColor } from './utils';

export function ConsultantsTab({ consultants }) {
  if (consultants.length === 0) {
    return (
      <Card className="p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance por Consultor</h3>
        <p className="text-gray-500 text-center py-8">Nenhum consultor encontrado</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Performance Table */}
      <Card className="p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance por Consultor</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Consultor</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Tarefas</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Concluídas</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Em Andamento</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Atrasadas</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Taxa de Conclusão</th>
              </tr>
            </thead>
            <tbody>
              {consultants.map((consultant, index) => (
                <tr 
                  key={consultant.user_id} 
                  className="border-b border-gray-100 hover:bg-gray-50"
                  data-testid={`consultant-row-${consultant.user_id}`}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      {index === 0 && consultants.length > 1 && (
                        <Award className="w-5 h-5 text-yellow-500" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{consultant.name}</p>
                        <p className="text-sm text-gray-500">{consultant.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center font-medium">{consultant.total_tasks}</td>
                  <td className="py-4 px-4 text-center">
                    <Badge className="bg-green-100 text-green-700">
                      {consultant.completed_tasks}
                    </Badge>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Badge className="bg-blue-100 text-blue-700">
                      {consultant.in_progress_tasks}
                    </Badge>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Badge className={consultant.overdue_tasks > 0 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}>
                      {consultant.overdue_tasks}
                    </Badge>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={consultant.completion_rate} 
                        className="h-2 flex-1"
                      />
                      <span className={`font-bold ${getCompletionColor(consultant.completion_rate)}`}>
                        {consultant.completion_rate}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top Performers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {consultants.slice(0, 3).map((consultant, index) => (
          <Card 
            key={consultant.user_id} 
            className={`p-5 border ${index === 0 ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                index === 0 ? 'bg-yellow-200' : index === 1 ? 'bg-gray-200' : 'bg-orange-200'
              }`}>
                <span className="font-bold text-lg">{index + 1}</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">{consultant.name}</p>
                <p className="text-sm text-gray-500">{consultant.total_tasks} tarefas</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Taxa de Conclusão</span>
              <span className={`text-xl font-bold ${getCompletionColor(consultant.completion_rate)}`}>
                {consultant.completion_rate}%
              </span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default ConsultantsTab;
